import React, { useState, useEffect } from 'react';
import styles from './ProductFormModal.module.css';
import { Product, CreateProductPayload, UpdateProductPayload } from '../../services/productService';
import { Sector } from '../../types/sector';
import { getSectorsByStore } from '../../services/sectorService';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (productData: CreateProductPayload | UpdateProductPayload, imageFile?: File) => void;
  initialData?: Product | null;
  isLoading?: boolean;
  selectedSectorId?: number | null;
  storeId?: number;
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  selectedSectorId,
  storeId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState<string | number>('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [sectorId, setSectorId] = useState<number | null>(null);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isLoadingSectors, setIsLoadingSectors] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen && storeId) {
      const fetchSectors = async () => {
        try {
          setIsLoadingSectors(true);
          const fetchedSectors = await getSectorsByStore(storeId);
          setSectors(fetchedSectors);
        } catch (error) {
          console.error('Failed to fetch sectors:', error);
        } finally {
          setIsLoadingSectors(false);
        }
      };
      fetchSectors();
    }
  }, [isOpen, storeId]);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setPrice(initialData.price !== undefined ? initialData.price : '');
      setImageUrl(initialData.image_url || '');
      setSectorId(initialData.sector_id || null);
    } else {
      setName('');
      setDescription('');
      setPrice('');
      setImageUrl('');
      setImageFile(null);
      setSectorId(selectedSectorId || null);
    }
  }, [initialData, isOpen, selectedSectorId]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const productData = {
      product_name: name,
      product_description: description,
      brief: description,
      price: parseFloat(price as string),
      image_url: imageUrl || undefined,
      sector_id: sectorId,
      measurement_unit_id: undefined,
    };
    onSubmit(productData, imageFile || undefined);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    } else {
      setImageFile(null);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (e.dataTransfer.files[0].type.startsWith('image/')) {
        setImageFile(e.dataTransfer.files[0]);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h3>{initialData ? 'Edit Product' : 'Add New Product'}</h3>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="productName">Name *</label>
            <input
              type="text"
              id="productName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="productDescription">Description *</label>
            <textarea
              id="productDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="productPrice">Price *</label>
            <input
              type="number"
              id="productPrice"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              min="0"
              step="0.01"
              disabled={isLoading}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Image</label>
            <div 
              className={`${styles.imageDropzone} ${isDragging ? styles.active : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => document.getElementById('productImageFile')?.click()}
            >
              <input
                type="file"
                id="productImageFile"
                accept="image/*"
                className={styles.hiddenInput}
                onChange={handleFileChange}
                disabled={isLoading}
              />
              {(imageFile || imageUrl) ? (
                <>
                  <img 
                    src={imageFile ? URL.createObjectURL(imageFile) : imageUrl} 
                    alt="Preview" 
                    className={styles.imagePreview}
                  />
                  <div className={styles.imageOverlay}>
                    <span>Click or drag to change</span>
                  </div>
                </>
              ) : (
                <div className={styles.imagePlaceholder}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>Click or drag image here</span>
                </div>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="productSector">Sector *</label>
            <select
              id="productSector"
              value={sectorId || ''}
              onChange={(e) => setSectorId(e.target.value ? parseInt(e.target.value, 10) : null)}
              required
              disabled={isLoading || isLoadingSectors}
            >
              <option value="">Select a sector</option>
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.id}>
                  {sector.sector_name}
                </option>
              ))}
            </select>
            {isLoadingSectors && <small>Loading sectors...</small>}
          </div>
          <div className={styles.formActions}>
            <button type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" disabled={isLoading || isLoadingSectors}>
              {isLoading ? 'Saving...' : (initialData ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal; 